import { CSSInterpolation } from '@emotion/css/create-instance'
import { cx } from '@emotion/css'
import React, { useCallback } from 'react'

import type { SemanticColorToken } from '../themes'
import { CssVars, NeutralColorToken, type ThemeColorToken } from '../themes'
import { Accordion, AccordionPhase } from './Accordion'
import { FoundationIcon } from './FoundationIcon'
import type { Icon24 } from './FoundationIcon/icons/directory'
import { IconButton } from './IconButton'
import { InternalElevate } from './internal/InternalElevate'
import { TextButton } from './TextButton'
import { TextColorOverride } from './TextColorOverride'
import { getColorValue, useTheme } from './ThemeProvider'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

type CallToActionProps =
  | {}
  | {
      /**
       * The text to use as a call to action.
       */
      callToAction: string
      /**
       * A callback to be called when the user clicks on the call to action.
       */
      onCallToActionClick: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
      ) => void
    }

type Props = {
  /**
   * The primary text to show in the `InfoBanner`.
   */
  text: React.ReactNode
  /**
   * An optional callback that will be called when the user has dismissed the `InfoBanner`.
   * Note that this is called after the `InfoBanner` has finished animating out, so it is safe
   * to immediately remove the `InfoBanner` from the DOM if you want. If no callback is
   * provided, the `InfoBanner` is not dismissable.
   */
  onDismiss?: () => void
  /**
   * If provided, this color will be used for the background of the `InfoBanner`. Defaults to
   * BG2 for light themes and elevated BG1 for dark themes.
   */
  backgroundColor?: SemanticColorToken | ThemeColorToken
  /**
   * If provided, this color will be used for the text and icons.
   */
  foregroundColor?: SemanticColorToken | ThemeColorToken
  /**
   * The icon to use in the `InfoBanner`.
   */
  iconType?: Icon24
  /**
   * Pass a CSS object if you need to override styles on the root element (which is outside the
   * collapsing area). Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
  /**
   * Pass a CSS object if you need to override styles on the visual banner element (which is
   * inside the collapsing area). Please do this sparingly.
   */
  overrideBannerStyles?: CSSInterpolation
} & CallToActionProps

function InfoBannerComponent({
  backgroundColor: backgroundColorProp,
  foregroundColor,
  iconType,
  internalElevateClassName,
  onDismiss,
  overrideBannerStyles,
  text,
  ...optionalProps
}: Props & {
  internalElevateClassName: string
}) {
  const theme = useTheme()
  const id = useUniqueId()

  const defaultBackgroundColor = theme.isDark
    ? NeutralColorToken.Bg1
    : NeutralColorToken.Bg2
  const backgroundColor = backgroundColorProp ?? defaultBackgroundColor

  const backgroundColorVariable = getColorValue(backgroundColor, theme)

  return (
    <TextColorOverride color={foregroundColor}>
      {({ rootClassName: textColorOverrideRootClassName }) => (
        <div
          aria-labelledby={id}
          className={cx(
            internalElevateClassName,
            textColorOverrideRootClassName
          )}
          css={[
            {
              alignItems: 'center',
              backgroundColor: backgroundColorVariable,
              borderRadius: CssVars.size.borderRadius,
              boxSizing: 'border-box',
              display: 'flex',
              padding: CssVars.size.gutterWidth,
              width: '100%',
            },
            overrideBannerStyles,
          ]}
          role="region"
        >
          {iconType && <FoundationIcon type={iconType} />}
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              paddingLeft: iconType ? CssVars.size.gutterWidth : 0,
              paddingRight: onDismiss ? CssVars.size.gutterWidth : 0,
            }}
            id={id}
          >
            <Type.Text color={foregroundColor} size="S">
              {text}
              {'callToAction' in optionalProps && (
                <>
                  {' '}
                  <TextButton
                    color={foregroundColor}
                    label={optionalProps.callToAction}
                    onClick={optionalProps.onCallToActionClick}
                    type="button"
                    variant="inline"
                  />
                </>
              )}
            </Type.Text>
          </div>
          {onDismiss && (
            <IconButton
              aria-label="Close"
              backgroundColor={backgroundColor}
              foregroundColor={foregroundColor}
              icon="Close16"
              onClick={onDismiss}
              type="button"
            />
          )}
        </div>
      )}
    </TextColorOverride>
  )
}

/**
 * An `InfoBanner` is a highly visible area used for short-term communication or upsells that
 * do not require user action. They should live within close proximity to the feature they
 * refer to. They are given an ARIA `role` of `"region"` and therefore will be surfaced
 * prominently to users using screen readers. As such, they should be used for important info.
 */
export function InfoBanner({
  foregroundColor = NeutralColorToken.Fg1,
  iconType,
  overrideRootStyles,
  overrideBannerStyles,
  onDismiss: onDismissProp,
  ...props
}: Props) {
  const [isVisible, setIsVisible] = React.useState(true)
  // When the user clicks the close button
  const onDismiss = useCallback(() => setIsVisible(false), [])

  // We want to defer calling the onDismiss prop callback until after the Accordion has
  // finished animating closed, so that the parent can't remove the whole InfoBanner from the
  // DOM before the animation completes.
  const onStateChange = useCallback(
    (isOpen: boolean, phase: AccordionPhase) => {
      if (!isOpen && phase === AccordionPhase.Stable) {
        onDismissProp?.()
      }
    },
    [onDismissProp]
  )

  return (
    <Accordion
      isOpen={isVisible}
      onStateChange={onStateChange}
      overrideRootStyles={overrideRootStyles}
    >
      <InternalElevate>
        {({ rootClassName }) => (
          <InfoBannerComponent
            foregroundColor={foregroundColor}
            iconType={iconType}
            internalElevateClassName={rootClassName}
            onDismiss={onDismissProp && onDismiss}
            overrideBannerStyles={overrideBannerStyles}
            {...props}
          />
        )}
      </InternalElevate>
    </Accordion>
  )
}
